<script setup lang="ts">
/**
 * BottomBar · 底栏七项（日程、空间、云电脑入口已移至顶栏）
 * ------------------------------------------------------------
 * 原型对应：#tabBar + TAB_OF_VIEW
 *
 * 硬约束：顺序固定、「我的」常驻；产研为产品研发工作台（原方案库），置于应用与架构之间；
 * 架构管理为独立功能，排在方案之后；FDE 交付为端到端交付工作台。
 * 重设计：emoji → SVG 线稿图标；激活态加渐变药丸底 + 图标描边加粗/填充；
 * hover 淡色背景；字号字重微调；间距优化。
 */
import { computed } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useI18n } from '@/design/i18n'
import { isModuleEnabled } from '@/shell/registry'

const router = useRouter()
const route = useRoute()
const { t } = useI18n()

export type TabItem = {
  key: string
  path: string
  iconKey: 'chat' | 'task' | 'apps' | 'sol' | 'arch' | 'fde' | 'mine'
  labelKey: 'chat' | 'task' | 'apps' | 'appsShort' | 'sol' | 'arch' | 'fde' | 'mine'
  /** 归属模块：未设置 = 底座常驻；设置后随模块开关显隐 */
  module?: string
}

const ALL_TABS: TabItem[] = [
  { key: 'chat',    path: '/chat',    iconKey: 'chat',    labelKey: 'chat' },
  { key: 'task',    path: '/tasks',   iconKey: 'task',    labelKey: 'task',    module: 'tasks' },
  { key: 'arch',    path: '/arch',    iconKey: 'arch',    labelKey: 'arch',    module: 'architecture' },
  { key: 'sol',     path: '/product/overview', iconKey: 'sol',    labelKey: 'sol', module: 'solution' },
  { key: 'apps',    path: '/apps',    iconKey: 'apps',    labelKey: 'appsShort', module: 'appcenter' },
  { key: 'fde',     path: '/fde',     iconKey: 'fde',     labelKey: 'fde',     module: 'fde' },
  { key: 'mine',    path: '/profile', iconKey: 'mine',    labelKey: 'mine' }
]

/* 只展示已启用模块的底栏入口（底座项常驻） */
const TABS = computed(() => ALL_TABS.filter(tab => !tab.module || isModuleEnabled(tab.module)))

function active(key: string): boolean {
  const tab = route.meta.tab as string | undefined
  return tab === key || (key === 'chat' && !tab)
}

/* SVG 图标库（线稿风格，统一 24×24 viewBox） */
const ICONS: Record<TabItem['iconKey'], string> = {
  chat:     '<path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"/>',
  task:     '<path d="M9 11l3 3L22 4" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"/><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"/>',
  apps:     '<rect x="3" y="3" width="7" height="7" rx="1.5" fill="none" stroke="currentColor" stroke-width="1.7"/><rect x="14" y="3" width="7" height="7" rx="1.5" fill="none" stroke="currentColor" stroke-width="1.7"/><rect x="3" y="14" width="7" height="7" rx="1.5" fill="none" stroke="currentColor" stroke-width="1.7"/><rect x="14" y="14" width="7" height="7" rx="1.5" fill="none" stroke="currentColor" stroke-width="1.7"/>',
  sol:      '<rect x="3" y="8" width="13" height="13" rx="2" fill="none" stroke="currentColor" stroke-width="1.7"/><path d="M8 8V5a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2h-3" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round"/><path d="M9.5 14.5l2 2 4-4.5" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"/>',
  arch:     '<rect x="9" y="2.5" width="6" height="5" rx="1" fill="none" stroke="currentColor" stroke-width="1.7"/><rect x="2.5" y="16.5" width="6" height="5" rx="1" fill="none" stroke="currentColor" stroke-width="1.7"/><rect x="15.5" y="16.5" width="6" height="5" rx="1" fill="none" stroke="currentColor" stroke-width="1.7"/><path d="M12 7.5v3M5.5 16.5v-3h13v3M12 10.5v3" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"/>',
  fde:      '<path d="M4.5 16.5c-1.5 1.26-2 5-2 5s3.74-.5 5-2c.71-.84.7-2.13-.09-2.91a2.18 2.18 0 0 0-2.91-.09z" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"/><path d="M12 15l-3-3a22 22 0 0 1 2-3.95A12.88 12.88 0 0 1 22 2c0 2.72-.78 7.5-6 11a22.35 22.35 0 0 1-4 2z" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"/><path d="M9 12H4s.55-3.03 2-4c1.62-1.08 5 0 5 0M12 15v5s3.03-.55 4-2c1.08-1.62 0-5 0-5" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"/>',
  mine:     '<circle cx="12" cy="8" r="4" fill="none" stroke="currentColor" stroke-width="1.7"/><path d="M4 21c0-4.4 3.6-8 8-8s8 3.6 8 8" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round"/>'
}
</script>

<template>
  <nav class="bottom-bar" role="tablist">
    <button v-for="tab in TABS" :key="tab.key"
            :class="['bb-item', { on: active(tab.key) }]"
            @click="router.push(tab.path)"
            :aria-label="t.nav[tab.labelKey]">
      <span class="bb-pill">
        <svg class="bb-ic" viewBox="0 0 24 24" width="20" height="20" aria-hidden="true"
             v-html="ICONS[tab.iconKey]" />
      </span>
      <span class="bb-lb">{{ t.nav[tab.labelKey] }}</span>
    </button>
  </nav>
</template>

<style scoped>
.bottom-bar {
  position: fixed;
  bottom: 0; left: 0; right: 0;
  height: 64px;
  display: flex;
  justify-content: center; gap: 4px;
  background: rgba(255, 255, 255, .96);
  -webkit-backdrop-filter: saturate(1.7) blur(14px);
  backdrop-filter: saturate(1.7) blur(14px);
  border-top: 1px solid var(--line-soft);
  box-shadow: 0 -4px 20px -20px rgba(20, 22, 26, .3);
  padding: 0 8px;
  z-index: 200;
}
.bb-item {
  position: relative;
  display: flex; flex-direction: column; align-items: center; justify-content: center;
  gap: 2px; width: 82px; height: 100%;
  border: none; background: none; cursor: pointer;
  color: var(--ink-4);
  transition: color .18s;
  padding: 0 4px;
}
.bb-item:hover { color: var(--ink-2); }
.bb-item:hover .bb-pill { background: var(--surface-3); }

/* 激活态：渐变药丸底 + 图标填充 */
.bb-item.on { color: var(--accent); font-weight: var(--fw-semibold); }
.bb-item.on .bb-pill {
  background: linear-gradient(135deg, color-mix(in oklab, var(--accent) 14%, transparent), color-mix(in oklab, var(--accent) 6%, transparent));
  border-radius: 12px;
}
.bb-item.on .bb-ic :deep(*) { stroke-width: 2.1; }

.bb-pill {
  display: grid; place-items: center;
  width: 36px; height: 30px;
  border-radius: 10px;
  transition: background .18s;
}
.bb-ic { display: block; }
.bb-lb {
  font-size: 10.6px; line-height: 1;
  letter-spacing: .2px;
}

@media (max-width: 1080px) {
  .bottom-bar { overflow-x: auto; justify-content: flex-start; gap: 0; }
  .bb-item { width: auto; min-width: 56px; flex: 1 0 56px; }
}
html[data-mode="dark"] .bottom-bar { background: rgba(14, 16, 19, .94); border-top-color: #2a2f38; }
</style>
